/**
 * @zendir/ui - PacketViewer Component
 *
 * Live telemetry packet viewer for spacecraft data analysis and monitoring.
 * Displays telemetry items with limits coloring, raw/converted/formatted views,
 * item details, and search filtering.
 *
 * Features:
 * - Table of telemetry items with name, value, limits status
 * - Limits coloring (blue, green, yellow, red) on values
 * - View modes: raw, converted, formatted, with-units
 * - Item search/filter
 * - Item details on click (type, conversion, limits, description)
 * - Stale data detection
 * - Configurable refresh rate display
 * - Compact mode for high-density displays
 *
 * @example
 * ```tsx
 * <PacketViewer
 *   target="INST"
 *   packet="HEALTH_STATUS"
 *   items={[
 *     { name: 'TEMP1', value: 72.5, limits: { state: 'GREEN', redLow: 0, yellowLow: 20, greenLow: 40, greenHigh: 80, yellowHigh: 90, redHigh: 100 } },
 *     { name: 'VOLTAGE', value: 28.1, units: 'V' },
 *   ]}
 * />
 * ```
 */

import React, { useState, useMemo, useCallback, memo } from "react";
import { useTheme } from "../../theme";

// =============================================================================
// Types
// =============================================================================

export type PacketItemLimitsState =
  | "BLUE"
  | "GREEN"
  | "YELLOW"
  | "YELLOW_LOW"
  | "YELLOW_HIGH"
  | "RED"
  | "RED_LOW"
  | "RED_HIGH"
  | "STALE"
  | null;
export type PacketViewMode = "raw" | "converted" | "formatted" | "with_units";

export interface PacketItemLimits {
  state: PacketItemLimitsState;
  redLow?: number;
  yellowLow?: number;
  greenLow?: number;
  greenHigh?: number;
  yellowHigh?: number;
  redHigh?: number;
}

export interface PacketItem {
  /** Item name (e.g., TEMP1, VOLTAGE) */
  name: string;
  /** Raw value */
  value: number | string | boolean;
  /** Converted value (after read conversion) */
  convertedValue?: number | string;
  /** Formatted value (with format string applied) */
  formattedValue?: string;
  /** Units string */
  units?: string;
  /** Limits definition and current state */
  limits?: PacketItemLimits;
  /** Item description */
  description?: string;
  /** Data type (e.g., UINT, INT, FLOAT, STRING, BLOCK, DERIVED) */
  dataType?: string;
  /** Bit size */
  bitSize?: number;
  /** Bit offset */
  bitOffset?: number;
  /** Whether this is a derived item */
  derived?: boolean;
  /** Conversion applied */
  conversion?: string;
  /** States map (for state items) */
  states?: Record<string, number | string>;
  /** Whether value is stale */
  stale?: boolean;
}

export interface PacketViewerProps {
  /** Target name */
  target?: string;
  /** Packet name */
  packet?: string;
  /** Array of packet items */
  items: PacketItem[];
  /** View mode (default: 'converted') */
  viewMode?: PacketViewMode;
  /** Height (default: 500) */
  height?: number | string;
  /** Show derived items last (default: true) */
  derivedLast?: boolean;
  /** Show search bar (default: true) */
  showSearch?: boolean;
  /** Show view mode selector (default: true) */
  showViewMode?: boolean;
  /** Compact mode */
  compact?: boolean;
  /** Called when an item is clicked */
  onItemClick?: (item: PacketItem) => void;
  /** CSS class */
  className?: string;
  /** Show outer container border (default: true). Set false when embedded inside a Container/card. */
  bordered?: boolean;
  /** Custom style overrides for the outer container */
  style?: React.CSSProperties;
}

// =============================================================================
// Helpers
// =============================================================================

function getLimitsColor(
  state: PacketItemLimitsState,
  tokens: ReturnType<typeof useTheme>["tokens"],
): string {
  switch (state) {
    case "RED":
    case "RED_LOW":
    case "RED_HIGH":
      return tokens.colors.status.critical;
    case "YELLOW":
    case "YELLOW_LOW":
    case "YELLOW_HIGH":
      return tokens.colors.status.caution;
    case "GREEN":
      return tokens.colors.status.normal;
    case "BLUE":
      return tokens.colors.status.standby;
    case "STALE":
      return tokens.colors.status.off;
    default:
      return tokens.colors.text.primary;
  }
}

function getLimitsBg(
  state: PacketItemLimitsState,
  tokens: ReturnType<typeof useTheme>["tokens"],
): string {
  const color = getLimitsColor(state, tokens);
  return state ? `${color}18` : "transparent";
}

/**
 * Return Astro UX-compliant status shape style for a limits state.
 * Maps packet limits levels to the canonical Astro status shapes:
 *   BLUE/STALE → off (small circle), GREEN → normal (filled circle),
 *   YELLOW → caution (square), RED → critical (triangle down via clip-path)
 */
function getLimitsShapeStyle(
  state: PacketItemLimitsState,
  color: string,
): React.CSSProperties {
  const base: React.CSSProperties = {
    width: 8,
    height: 8,
    display: "inline-block",
    background: color,
    boxShadow: `0 0 4px ${color}66`,
  };
  switch (state) {
    case "GREEN":
      return { ...base, borderRadius: "50%" }; // normal → circle
    case "YELLOW":
    case "YELLOW_LOW":
    case "YELLOW_HIGH":
      return { ...base, borderRadius: "1px" }; // caution → square
    case "RED":
    case "RED_LOW":
    case "RED_HIGH":
      return {
        ...base,
        borderRadius: 0, // critical → triangle down
        clipPath: "polygon(50% 100%, 0% 0%, 100% 0%)",
      };
    case "BLUE":
      return {
        ...base,
        borderRadius: "50%",
        width: 6,
        height: 6, // standby → hollow ring
        background: "transparent",
        border: `2px solid ${color}`,
        boxShadow: "none",
      };
    case "STALE":
      return {
        ...base,
        borderRadius: "50%",
        width: 5,
        height: 5, // off → small dot
        opacity: 0.6,
      };
    default:
      return { ...base, borderRadius: "50%" };
  }
}

function getDisplayValue(item: PacketItem, mode: PacketViewMode): string {
  switch (mode) {
    case "raw":
      return String(item.value);
    case "converted":
      return String(item.convertedValue ?? item.value);
    case "formatted":
      return item.formattedValue ?? String(item.convertedValue ?? item.value);
    case "with_units": {
      const val =
        item.formattedValue ?? String(item.convertedValue ?? item.value);
      return item.units ? `${val} ${item.units}` : val;
    }
  }
}

// =============================================================================
// Component
// =============================================================================

export const PacketViewer = memo(function PacketViewer({
  target,
  packet,
  items,
  viewMode: initialViewMode = "converted",
  height = 500,
  derivedLast = true,
  showSearch = true,
  showViewMode = true,
  compact = false,
  onItemClick,
  className = "",
  bordered = true,
  style: styleProp,
}: PacketViewerProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [selectedItem, setSelectedItem] = useState<PacketItem | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(240);
  const listRef = React.useRef<HTMLDivElement>(null);

  const sortedItems = useMemo(() => {
    let filtered = items;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = items.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.description && i.description.toLowerCase().includes(q)),
      );
    }
    if (derivedLast) {
      const regular = filtered.filter((i) => !i.derived);
      const derived = filtered.filter((i) => i.derived);
      return [...regular, ...derived];
    }
    return filtered;
  }, [items, searchQuery, derivedLast]);

  const handleItemClick = useCallback(
    (item: PacketItem) => {
      setSelectedItem(item);
      setShowDetails(true);
      onItemClick?.(item);
    },
    [onItemClick],
  );

  const rowHeight = compact ? 26 : 32;
  const overscanRows = 12;
  const totalRows = sortedItems.length;
  const startRow = Math.max(
    0,
    Math.floor(scrollTop / rowHeight) - overscanRows,
  );
  const visibleRows = Math.ceil(viewportHeight / rowHeight) + overscanRows * 2;
  const endRow = Math.min(totalRows, startRow + visibleRows);
  const visibleItems = useMemo(
    () => sortedItems.slice(startRow, endRow),
    [sortedItems, startRow, endRow],
  );

  React.useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;

    const updateHeight = () =>
      setViewportHeight(Math.max(rowHeight * 4, el.clientHeight || 240));
    updateHeight();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [rowHeight]);

  return (
    <div
      className={`packet-viewer ${className}`}
      style={{
        height: typeof height === "number" ? height : undefined,
        background: tokens.colors.background.base,
        ...(bordered
          ? (tokens.colors.border.cardStyle ?? {
              border: `1px solid ${tokens.colors.border.muted}`,
            })
          : { border: "none" }),
        borderRadius: tokens.borderRadius.lg,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        fontFamily: tokens.typography.fontFamily.primary,
        color: tokens.colors.text.primary,
        backdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
        WebkitBackdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
        ...styleProp,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "8px 12px",
          borderBottom: `1px solid ${tokens.colors.border.muted}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: tokens.colors.background.surface,
          backdropFilter: "blur(8px)",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {target && (
            <span
              style={{
                background: `${tokens.colors.interactive.default}22`,
                color: tokens.colors.interactive.default,
                padding: "2px 8px",
                borderRadius: tokens.borderRadius.md,
                fontSize: tokens.typography.fontSize.xxs,
                fontWeight: 600,
              }}
            >
              {target}
            </span>
          )}
          {packet && (
            <span
              style={{
                fontWeight: 600,
                fontSize: tokens.typography.fontSize.sm,
              }}
            >
              {packet}
            </span>
          )}
          <span
            style={{
              fontSize: tokens.typography.fontSize.xxs,
              color: tokens.colors.text.muted,
            }}
          >
            {sortedItems.length} items
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {showViewMode && (
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as PacketViewMode)}
              aria-label="Packet view mode"
              style={{
                background: tokens.colors.background.base,
                color: tokens.colors.text.primary,
                border: `1px solid ${tokens.colors.border.muted}`,
                borderRadius: tokens.borderRadius.md,
                padding: "3px 8px",
                fontSize: tokens.typography.fontSize.xxs,
              }}
            >
              <option value="raw">Raw</option>
              <option value="converted">Converted</option>
              <option value="formatted">Formatted</option>
              <option value="with_units">With Units</option>
            </select>
          )}
          {showSearch && (
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter items..."
              aria-label="Filter packet items"
              style={{
                background: tokens.colors.background.base,
                color: tokens.colors.text.primary,
                border: `1px solid ${tokens.colors.border.muted}`,
                borderRadius: tokens.borderRadius.md,
                padding: "3px 8px",
                fontSize: tokens.typography.fontSize.xxs,
                outline: "none",
                minWidth: 120,
              }}
            />
          )}
        </div>
      </div>

      {/* Column headers */}
      <div
        role="row"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr auto",
          padding: `${tokens.spacing.xs} ${tokens.spacing.smd}`,
          borderBottom: `1px solid ${tokens.colors.border.muted}`,
          background: tokens.colors.background.surface,
          fontSize: tokens.typography.fontSize.xxs,
          color: tokens.colors.text.muted,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontWeight: tokens.typography.fontWeight.medium,
        }}
      >
        <span role="columnheader">Item</span>
        <span role="columnheader" style={{ textAlign: "right" }}>
          Value
        </span>
        <span role="columnheader" style={{ minWidth: 50, textAlign: "center" }}>
          Limits
        </span>
      </div>

      {/* Items list */}
      <div
        ref={listRef}
        role="rowgroup"
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        style={{ flex: 1, overflow: "auto" }}
      >
        <div style={{ height: totalRows * rowHeight, position: "relative" }}>
          <div
            style={{
              position: "absolute",
              top: startRow * rowHeight,
              left: 0,
              right: 0,
            }}
          >
            {visibleItems.map((item, visibleIdx) => {
              const idx = startRow + visibleIdx;
              const limitsColor = item.stale
                ? tokens.colors.status.off
                : getLimitsColor(item.limits?.state ?? null, tokens);
              const limitsBg = item.stale
                ? `${tokens.colors.status.off}14`
                : getLimitsBg(item.limits?.state ?? null, tokens);

              return (
                <div
                  role="row"
                  tabIndex={0}
                  key={item.name + idx}
                  onClick={() => handleItemClick(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleItemClick(item);
                    }
                  }}
                  aria-label={`${item.name}: ${getDisplayValue(item, viewMode)}${item.limits?.state ? `, ${item.limits.state}` : ""}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr auto",
                    padding: `0 ${tokens.spacing.smd}`,
                    height: rowHeight,
                    alignItems: "center",
                    borderBottom: `1px solid ${tokens.colors.border.muted}`,
                    cursor: "pointer",
                    transition: "background 150ms ease",
                    background: item.derived
                      ? `${tokens.colors.background.surface}44`
                      : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background =
                      tokens.colors.background.surface;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background =
                      item.derived
                        ? `${tokens.colors.background.surface}44`
                        : "transparent";
                  }}
                >
                  {/* Item name */}
                  <span
                    style={{
                      fontSize: compact
                        ? tokens.typography.fontSize.xxs
                        : tokens.typography.fontSize.xs,
                      fontWeight: 500,
                      color: item.derived
                        ? tokens.colors.text.muted
                        : tokens.colors.text.primary,
                      fontStyle: item.derived ? "italic" : "normal",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.name}
                  </span>

                  {/* Value */}
                  <span
                    style={{
                      textAlign: "right",
                      fontFamily: tokens.typography.fontFamily.mono,
                      fontSize: compact
                        ? tokens.typography.fontSize.xxs
                        : tokens.typography.fontSize.xs,
                      fontWeight: 600,
                      color: limitsColor,
                      background: limitsBg,
                      padding: "2px 8px",
                      borderRadius: tokens.borderRadius.sm,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getDisplayValue(item, viewMode)}
                  </span>

                  {/* Limits indicator — Astro UX status shape */}
                  <div
                    style={{
                      minWidth: 50,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {item.limits?.state && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <span
                          style={getLimitsShapeStyle(
                            item.limits.state,
                            limitsColor,
                          )}
                        />
                        <span
                          style={{
                            fontSize: tokens.typography.fontSize.micro,
                            color: limitsColor,
                            fontWeight: 600,
                          }}
                        >
                          {item.limits.state.replace(/_/g, " ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Details panel */}
      {showDetails && selectedItem && (
        <div
          style={{
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            background: tokens.colors.background.surface,
            padding: 12,
            maxHeight: 200,
            overflow: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: tokens.spacing.sm,
            }}
          >
            <span
              style={{
                fontWeight: tokens.typography.fontWeight.bold,
                fontSize: tokens.typography.fontSize.sm,
              }}
            >
              {selectedItem.name}
            </span>
            <button
              onClick={() => setShowDetails(false)}
              aria-label="Close details panel"
              style={{
                background: "none",
                border: "none",
                color: tokens.colors.text.muted,
                cursor: "pointer",
                fontSize: tokens.typography.fontSize.lg,
                padding: tokens.spacing.xs,
                lineHeight: 1,
              }}
            >
              &#x2715;
            </button>
          </div>
          {selectedItem.description && (
            <p
              style={{
                color: tokens.colors.text.secondary,
                fontSize: tokens.typography.fontSize.xs,
                marginBottom: 8,
              }}
            >
              {selectedItem.description}
            </p>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "4px 12px",
              fontSize: tokens.typography.fontSize.xxs,
              fontFamily: tokens.typography.fontFamily.mono,
            }}
          >
            <span style={{ color: tokens.colors.text.muted }}>Raw:</span>
            <span>{String(selectedItem.value)}</span>
            {selectedItem.convertedValue !== undefined && (
              <>
                <span style={{ color: tokens.colors.text.muted }}>
                  Converted:
                </span>
                <span>{String(selectedItem.convertedValue)}</span>
              </>
            )}
            {selectedItem.units && (
              <>
                <span style={{ color: tokens.colors.text.muted }}>Units:</span>
                <span>{selectedItem.units}</span>
              </>
            )}
            {selectedItem.dataType && (
              <>
                <span style={{ color: tokens.colors.text.muted }}>Type:</span>
                <span>{selectedItem.dataType}</span>
              </>
            )}
            {selectedItem.bitSize !== undefined && (
              <>
                <span style={{ color: tokens.colors.text.muted }}>
                  Bit Size:
                </span>
                <span>{selectedItem.bitSize}</span>
              </>
            )}
            {selectedItem.bitOffset !== undefined && (
              <>
                <span style={{ color: tokens.colors.text.muted }}>
                  Bit Offset:
                </span>
                <span>{selectedItem.bitOffset}</span>
              </>
            )}
            {selectedItem.conversion && (
              <>
                <span style={{ color: tokens.colors.text.muted }}>
                  Conversion:
                </span>
                <span>{selectedItem.conversion}</span>
              </>
            )}
            {selectedItem.limits && (
              <>
                <span style={{ color: tokens.colors.text.muted }}>
                  Limits State:
                </span>
                <span
                  style={{
                    color: getLimitsColor(selectedItem.limits.state, tokens),
                  }}
                >
                  {selectedItem.limits.state}
                </span>
                {selectedItem.limits.redLow !== undefined && (
                  <>
                    <span style={{ color: tokens.colors.text.muted }}>
                      Red Low:
                    </span>
                    <span>{selectedItem.limits.redLow}</span>
                  </>
                )}
                {selectedItem.limits.yellowLow !== undefined && (
                  <>
                    <span style={{ color: tokens.colors.text.muted }}>
                      Yellow Low:
                    </span>
                    <span>{selectedItem.limits.yellowLow}</span>
                  </>
                )}
                {selectedItem.limits.greenHigh !== undefined && (
                  <>
                    <span style={{ color: tokens.colors.text.muted }}>
                      Green High:
                    </span>
                    <span>{selectedItem.limits.greenHigh}</span>
                  </>
                )}
                {selectedItem.limits.yellowHigh !== undefined && (
                  <>
                    <span style={{ color: tokens.colors.text.muted }}>
                      Yellow High:
                    </span>
                    <span>{selectedItem.limits.yellowHigh}</span>
                  </>
                )}
                {selectedItem.limits.redHigh !== undefined && (
                  <>
                    <span style={{ color: tokens.colors.text.muted }}>
                      Red High:
                    </span>
                    <span>{selectedItem.limits.redHigh}</span>
                  </>
                )}
              </>
            )}
            {selectedItem.states && (
              <>
                <span style={{ color: tokens.colors.text.muted }}>States:</span>
                <span>
                  {Object.entries(selectedItem.states)
                    .map(([k, v]) => `${k}=${v}`)
                    .join(", ")}
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default PacketViewer;
